import type { Condition, DesignElement, MergeField } from '@bre-ticketing/ticket-design';
import { Button } from '../ui';

/**
 * The properties panel (D-89, PART 11, 13).
 *
 * Whatever is selected, this is where its details are. The controls are the
 * design's vocabulary made visible: a font from three, a size in points, an
 * alignment, a merge field from the registry — and a rule made of a field, a
 * comparison and a value, because a condition here is never an expression
 * somebody types.
 */

const INPUT = 'w-full rounded border border-line bg-surface px-2 py-1.5 text-sm';
const LABEL = 'text-xs font-semibold text-ink-400 uppercase';

const OPS: { value: string; label: string; needsValue: 'none' | 'one' | 'many' }[] = [
  { value: 'EXISTS', label: 'has something in it', needsValue: 'none' },
  { value: 'NOT_EXISTS', label: 'is empty', needsValue: 'none' },
  { value: 'EQUALS', label: 'is exactly', needsValue: 'one' },
  { value: 'NOT_EQUALS', label: 'is not', needsValue: 'one' },
  { value: 'CONTAINS', label: 'contains', needsValue: 'one' },
  { value: 'IN', label: 'is one of', needsValue: 'many' },
  { value: 'NOT_IN', label: 'is none of', needsValue: 'many' },
];

export function Properties({
  element,
  fields,
  onChange,
  onDelete,
  onDuplicate,
  readOnly,
}: {
  element: DesignElement | null;
  fields: MergeField[];
  onChange: (patch: Partial<DesignElement>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  readOnly: boolean;
}) {
  if (!element) {
    return <p className="px-4 py-3 text-sm text-ink-600">Nothing selected. Click something on the ticket, or add an element.</p>;
  }

  const patch = (values: Record<string, unknown>) => onChange(values as Partial<DesignElement>);
  const inches = (mils: number) => (mils / 1000).toFixed(2);
  const fromInches = (value: string) => Math.round(Number(value) * 1000);
  const isText = element.kind === 'TEXT' || element.kind === 'FIELD';

  return (
    <div className="space-y-4 px-4 py-3">
      <div className="flex items-center gap-2">
        <input value={element.name ?? ''} onChange={(e) => patch({ name: e.target.value })} placeholder={element.kind.toLowerCase()} aria-label="Name" className={INPUT} disabled={readOnly} />
        <label className="flex shrink-0 items-center gap-1 text-xs text-ink-600">
          <input type="checkbox" checked={element.locked === true} onChange={(e) => patch({ locked: e.target.checked })} disabled={readOnly} />
          Lock
        </label>
      </div>

      {/* Position and size, in inches, because that is what stock is measured in. */}
      <div className="grid grid-cols-4 gap-2">
        {([
          ['x_mils', 'X'],
          ['y_mils', 'Y'],
          ['width_mils', 'W'],
          ['height_mils', 'H'],
        ] as const).map(([key, label]) => (
          <label key={key} className="block">
            <span className={LABEL}>{label}</span>
            <input type="number" step="0.01" value={inches(element[key])} onChange={(e) => patch({ [key]: fromInches(e.target.value) })} className={INPUT} disabled={readOnly || element.locked} />
          </label>
        ))}
      </div>

      {element.kind === 'FIELD' ? (
        <label className="block">
          <span className={LABEL}>What it prints</span>
          <select value={element.field} onChange={(e) => patch({ field: e.target.value })} className={INPUT} disabled={readOnly}>
            {fields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.group} · {field.label}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-ink-400">{fields.find((field) => field.key === element.field)?.description}</span>
        </label>
      ) : null}

      {element.kind === 'TEXT' ? (
        <label className="block">
          <span className={LABEL}>Text</span>
          <textarea value={element.text} onChange={(e) => patch({ text: e.target.value })} rows={2} className={INPUT} disabled={readOnly} />
        </label>
      ) : null}

      {element.kind === 'FIELD' ? (
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className={LABEL}>Before</span>
            <input value={element.prefix ?? ''} onChange={(e) => patch({ prefix: e.target.value || null })} className={INPUT} disabled={readOnly} />
          </label>
          <label className="block">
            <span className={LABEL}>After</span>
            <input value={element.suffix ?? ''} onChange={(e) => patch({ suffix: e.target.value || null })} className={INPUT} disabled={readOnly} />
          </label>
          <label className="block">
            <span className={LABEL}>If empty</span>
            <input value={element.fallback ?? ''} onChange={(e) => patch({ fallback: e.target.value || null })} placeholder="nothing" className={INPUT} disabled={readOnly} />
          </label>
        </div>
      ) : null}

      {isText ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className={LABEL}>Font</span>
              <select value={element.font} onChange={(e) => patch({ font: e.target.value })} className={INPUT} disabled={readOnly}>
                <option value="SANS">Sans</option>
                <option value="SERIF">Serif</option>
                <option value="MONO">Monospace</option>
              </select>
            </label>
            <label className="block">
              <span className={LABEL}>Size (pt)</span>
              <input type="number" min={5} max={96} value={element.size_pt} onChange={(e) => patch({ size_pt: Number(e.target.value) })} className={INPUT} disabled={readOnly} />
            </label>
            <label className="block">
              <span className={LABEL}>Weight</span>
              <select value={element.weight} onChange={(e) => patch({ weight: e.target.value })} className={INPUT} disabled={readOnly}>
                <option value="NORMAL">Normal</option>
                <option value="BOLD">Bold</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className={LABEL}>Align</span>
              <select value={element.align} onChange={(e) => patch({ align: e.target.value })} className={INPUT} disabled={readOnly}>
                <option value="LEFT">Left</option>
                <option value="CENTER">Center</option>
                <option value="RIGHT">Right</option>
              </select>
            </label>
            <label className="block">
              <span className={LABEL}>Shrink to</span>
              <input
                type="number"
                min={5}
                max={96}
                value={element.min_size_pt ?? ''}
                placeholder="no"
                onChange={(e) => patch({ min_size_pt: e.target.value === '' ? null : Number(e.target.value) })}
                className={INPUT}
                disabled={readOnly}
              />
            </label>
            <label className="block">
              <span className={LABEL}>Lines</span>
              <input
                type="number"
                min={1}
                max={20}
                value={element.max_lines ?? ''}
                placeholder="fit"
                onChange={(e) => patch({ max_lines: e.target.value === '' ? null : Number(e.target.value) })}
                className={INPUT}
                disabled={readOnly}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={element.uppercase === true} onChange={(e) => patch({ uppercase: e.target.checked })} disabled={readOnly} />
            All capitals
          </label>
        </>
      ) : null}

      {element.kind === 'QR' ? (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={LABEL}>Carries</span>
            <select value={element.source} onChange={(e) => patch({ source: e.target.value })} className={INPUT} disabled={readOnly}>
              <option value="CREDENTIAL">The admission credential</option>
              <option value="SHORT_CODE">The short code</option>
              <option value="ORDER_URL">A link to the order</option>
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>Blank space (in)</span>
            <input
              type="number"
              step="0.01"
              value={((element.quiet_zone_mils ?? 40) / 1000).toFixed(2)}
              onChange={(e) => patch({ quiet_zone_mils: Math.round(Number(e.target.value) * 1000) })}
              className={INPUT}
              disabled={readOnly}
            />
          </label>
        </div>
      ) : null}

      {element.kind === 'BARCODE' ? (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={LABEL}>Symbology</span>
            <select value={element.symbology} onChange={(e) => patch({ symbology: e.target.value })} className={INPUT} disabled={readOnly}>
              <option value="CODE128">Code 128</option>
              <option value="CODE39">Code 39</option>
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>Carries</span>
            <select value={element.source} onChange={(e) => patch({ source: e.target.value })} className={INPUT} disabled={readOnly}>
              <option value="SHORT_CODE">The short code</option>
              <option value="ORDER_NUMBER">The order number</option>
            </select>
          </label>
        </div>
      ) : null}

      {element.kind === 'BOX' ? (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={LABEL}>Fill</span>
            <input type="color" value={element.fill_hex ?? '#ffffff'} onChange={(e) => patch({ fill_hex: e.target.value })} className={INPUT} disabled={readOnly} />
          </label>
          <label className="block">
            <span className={LABEL}>Border</span>
            <input type="color" value={element.border_hex ?? '#0f172a'} onChange={(e) => patch({ border_hex: e.target.value })} className={INPUT} disabled={readOnly} />
          </label>
        </div>
      ) : null}

      {element.kind === 'SPONSOR' ? (
        <label className="block">
          <span className={LABEL}>Which logo</span>
          <select value={element.variant} onChange={(e) => patch({ variant: e.target.value })} className={INPUT} disabled={readOnly}>
            <option value="COLOR">Full color</option>
            <option value="REVERSED">Reversed</option>
            <option value="BLACK">Black</option>
            <option value="WHITE">White</option>
          </select>
        </label>
      ) : null}

      <ConditionEditor condition={element.visible_when ?? null} fields={fields} onChange={(condition) => patch({ visible_when: condition })} readOnly={readOnly} />

      {!readOnly ? (
        <div className="flex gap-2 border-t border-line pt-3">
          <Button onClick={onDuplicate}>Duplicate</Button>
          <Button variant="danger" onClick={onDelete}>
            Delete
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** One rule: a field, a comparison, a value. Combinations are built by nesting on the server side. */
function ConditionEditor({ condition, fields, onChange, readOnly }: { condition: Condition | null; fields: MergeField[]; onChange: (condition: Condition | null) => void; readOnly: boolean }) {
  const simple = condition && 'field' in condition ? condition : null;
  const op = simple ? OPS.find((candidate) => candidate.value === simple.op) : null;

  return (
    <div className="rounded border border-line p-3">
      <p className={LABEL}>Only show this when</p>
      {condition && !simple ? (
        <p className="mt-1 text-sm text-ink-600">This element has a combined rule set elsewhere. Clearing it here removes the whole rule.</p>
      ) : null}
      <div className="mt-2 grid gap-2">
        <select
          value={simple?.field ?? ''}
          onChange={(e) => (e.target.value === '' ? onChange(null) : onChange({ field: e.target.value, op: 'EXISTS' }))}
          className={INPUT}
          disabled={readOnly}
          aria-label="Field the rule looks at"
        >
          <option value="">Always show it</option>
          {fields.map((field) => (
            <option key={field.key} value={field.key}>
              {field.group} · {field.label}
            </option>
          ))}
        </select>
        {simple ? (
          <select
            value={simple.op}
            onChange={(e) => {
              const next = OPS.find((candidate) => candidate.value === e.target.value)!;
              if (next.needsValue === 'none') onChange({ field: simple.field, op: next.value as 'EXISTS' | 'NOT_EXISTS' });
              else if (next.needsValue === 'many') onChange({ field: simple.field, op: next.value as 'IN' | 'NOT_IN', values: [] });
              else onChange({ field: simple.field, op: next.value as 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS', value: '' });
            }}
            className={INPUT}
            disabled={readOnly}
            aria-label="Comparison"
          >
            {OPS.map((candidate) => (
              <option key={candidate.value} value={candidate.value}>
                {candidate.label}
              </option>
            ))}
          </select>
        ) : null}
        {simple && op?.needsValue === 'one' ? (
          <input value={'value' in simple ? simple.value : ''} onChange={(e) => onChange({ field: simple.field, op: simple.op as 'EQUALS', value: e.target.value })} className={INPUT} disabled={readOnly} aria-label="Value" />
        ) : null}
        {simple && op?.needsValue === 'many' ? (
          <input
            value={'values' in simple ? simple.values.join(', ') : ''}
            onChange={(e) =>
              onChange({
                field: simple.field,
                op: simple.op as 'IN',
                values: e.target.value
                  .split(',')
                  .map((value) => value.trim())
                  .filter(Boolean),
              })
            }
            placeholder="Adult, Student, Senior"
            className={INPUT}
            disabled={readOnly}
            aria-label="Values, separated by commas"
          />
        ) : null}
      </div>
    </div>
  );
}
